import { createFileRoute } from '@tanstack/react-router'
import { Home, Settings } from 'lucide-react'
import { useEffect, useMemo } from 'react'
import { AccountDetailsForm } from '@/features/account/components/AccountDetailsForm'
import { useTenant } from '@/features/tenants/contexts/TenantContext'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/shared/components/ui/breadcrumb'
import { Card, CardContent } from '@/shared/components/ui/card'
import { Skeleton } from '@/shared/components/ui/skeleton'
import { useBreadcrumbs } from '@/shared/hooks/useBreadcrumbs'

export const Route = createFileRoute('/_auth/settings/account')({
  component: AccountSettingsPage,
})

function AccountSettingsPage() {
  const { isLoadingCurrentTenant } = useTenant()
  const { setBreadcrumbs } = useBreadcrumbs()

  const breadcrumbsContent = useMemo(
    () => (
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink to='/dashboard' className='flex items-center gap-1'>
              <Home className='w-4 h-4' />
              Dashboard
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <span className='flex items-center gap-1 text-muted-foreground'>
              <Settings className='w-4 h-4' />
              Settings
            </span>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>Account</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>
    ),
    []
  )

  useEffect(() => {
    setBreadcrumbs(breadcrumbsContent)
  }, [setBreadcrumbs, breadcrumbsContent])

  if (isLoadingCurrentTenant) {
    return (
      <div className='container mx-auto py-6'>
        <div className='space-y-6'>
          {/* Header Skeleton */}
          <div className='max-w-3xl mx-auto p-6'>
            <div className='flex flex-col gap-4 md:flex-row md:justify-between md:items-start'>
              <div>
                <Skeleton className='h-8 w-48 mb-2' />
                <Skeleton className='h-4 w-96' />
              </div>
            </div>
          </div>

          {/* Form Skeleton */}
          <div className='max-w-3xl mx-auto px-6'>
            <Card className='bg-transparent'>
              <CardContent className='space-y-10 pt-6'>
                {/* Account ID Skeleton */}
                <div className='space-y-2'>
                  <Skeleton className='h-4 w-20' />
                  <Skeleton className='h-4 w-32' />
                </div>

                {/* Organization Name Skeleton */}
                <div className='space-y-2'>
                  <Skeleton className='h-4 w-32' />
                  <div className='w-1/2'>
                    <Skeleton className='h-10 w-full' />
                  </div>
                </div>

                {/* Organization Logo Skeleton */}
                <div className='space-y-2'>
                  <Skeleton className='h-4 w-28' />
                  <div className='space-y-3'>
                    <Skeleton className='h-20 w-20 rounded-lg' />
                    <div className='flex gap-2'>
                      <Skeleton className='h-8 w-20' />
                    </div>
                    <Skeleton className='h-3 w-48' />
                  </div>
                </div>

                {/* Timezone Skeleton */}
                <div className='space-y-2'>
                  <Skeleton className='h-4 w-20' />
                  <div className='w-1/2'>
                    <Skeleton className='h-10 w-full' />
                  </div>
                </div>

                {/* Save Button Skeleton */}
                <div className='flex justify-end pt-4'>
                  <Skeleton className='h-10 w-32' />
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className='container mx-auto py-6'>
      <AccountDetailsForm />
    </div>
  )
}
